import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { growthKeys } from '@/data/queryKeys';
import { useNotify } from '@/data/notifications';
import type { MarketingCallOutcome } from '@/data/marketingApiTypes';
import {
  CALL_TRACKING_PAGE_SIZE,
  classifyCall,
  exportCallsCsv,
  fetchCallDetail,
  fetchCallTracking,
  fetchTrackingNumbers,
} from '../api';

export function useCallTracking(page = 1, limit = CALL_TRACKING_PAGE_SIZE) {
  return useGrowthQuery({
    queryKey: growthKeys.calls.list({ page, limit }),
    queryFn: () => fetchCallTracking({ page, limit }),
    placeholderData: keepPreviousData,
  });
}

export function useCallDetail(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.calls.detail(id),
    queryFn: () => fetchCallDetail(id),
    enabled: enabled && Boolean(id),
  });
}

export function useTrackingNumbers() {
  return useGrowthQuery({
    queryKey: growthKeys.calls.trackingNumbers(),
    queryFn: fetchTrackingNumbers,
  });
}

export function useCallTrackingMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'calls'] });

  const classify = useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: MarketingCallOutcome }) =>
      classifyCall(id, outcome),
    onSuccess: () => {
      notify.success('Call reclassified');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const exportCsv = useMutation({
    mutationFn: exportCallsCsv,
    onSuccess: () => notify.success('Call export started'),
    onError: (e) => notify.error(e),
  });

  return { classify, exportCsv };
}
